import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Plus, Star, Trash2, X } from 'lucide-react';
import { transitPackagingService, SaveTransitPackagingRequest } from '../../../../../services/transitPackagingService';
import { TransitPackagingDto } from '../../../../../types/shipmentLine';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { NumericInput } from '../../../../common/inputs';

interface TransitPackagingDataTableProps {
  refreshTrigger?: number;
}

// WarehouseTable krever rader med `id` (TableDataRow) — speiler transitPackagingID
type TransitPackagingRow = TransitPackagingDto & { id: number };

/**
 * Lastbærer-administrasjon (TransitPackaging) — Teamwork 36334219.
 *
 * CRUD-flate for lastbærerne som brukes i Transport-detaljer (ShipmentLine),
 * ved plukk mot pall og til TA/fakturering:
 *  - Mål (L/B/H cm), egenvekt/tara (kg) og volum (m³) prefyller kollilinjer og
 *    inngår i bruttovekt-beregningen ved plukk mot pall.
 *  - ExternalID kobler bæreren til ERP-artikkel (fakturering, 36334106).
 *  - «Sett som standard» håndhever ÉN default per selskap (rydder gamle data
 *    der alle sto som default).
 */
export const TransitPackagingDataTable: React.FC<TransitPackagingDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  const [data, setData] = useState<TransitPackagingRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<{ descr: string; weight: string; length: string; width: string; height: string; externalID: string }>({
    descr: '', weight: '', length: '', width: '', height: '', externalID: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyNo) return;
    setIsLoading(true);
    setError(null);
    try {
      const items = await transitPackagingService.getAll();
      setData(items.map(i => ({ ...i, id: i.transitPackagingID })));
    } catch (err: any) {
      console.error('Error loading transit packaging:', err);
      setError(t('manager.modules.adminHost.maintenance.transitPackaging.loadError', {}, 'Kunne ikke laste lastbærere. Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, t]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) loadData();
  }, [refreshTrigger, loadData]);

  const toRequest = (row: TransitPackagingRow, edited: Partial<Record<keyof TransitPackagingDto, unknown>>): SaveTransitPackagingRequest => ({
    transitPackagingDescr: (edited.transitPackagingDescr as string) ?? row.transitPackagingDescr,
    weight: (edited.weight as number) ?? row.weight,
    volume: (edited.volume as number) ?? row.volume,
    height: (edited.height as number) ?? row.height,
    width: (edited.width as number) ?? row.width,
    length: (edited.length as number) ?? row.length,
    externalID: (edited.externalID as string) ?? row.externalID
  });

  const inlineEditConfig = useInlineEdit<TransitPackagingRow>({
    getRowId: (row) => row.transitPackagingID,
    onSave: async (originalRow, editedData) => {
      await transitPackagingService.update(originalRow.transitPackagingID, toRequest(originalRow, editedData));
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminHost.maintenance.transitPackaging.saveError', {}, 'Feil ved lagring av lastbærer'))
  });

  const columnFactory = createColumnFactory<TransitPackagingRow>();

  const columns: ColumnDefinition<TransitPackagingRow>[] = useMemo(() => [
    columnFactory.createNumberColumn('transitPackagingID', 'transitPackagingID', t('manager.modules.adminHost.maintenance.transitPackaging.fields.id', {}, 'ID'), {
      minWidth: 70,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('transitPackagingDescr', 'transitPackagingDescr', t('manager.modules.adminHost.maintenance.transitPackaging.fields.descr', {}, 'Beskrivelse'), {
      minWidth: 180,
      sortable: true,
      filterType: 'both',
      editable: true
    }),
    columnFactory.createNumberColumn('weight', 'weight', t('manager.modules.adminHost.maintenance.transitPackaging.fields.weight', {}, 'Egenvekt (kg)'), {
      minWidth: 110,
      sortable: true,
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),
    columnFactory.createNumberColumn('length', 'length', t('manager.modules.adminHost.maintenance.transitPackaging.fields.length', {}, 'Lengde (cm)'), {
      minWidth: 100,
      sortable: true,
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),
    columnFactory.createNumberColumn('width', 'width', t('manager.modules.adminHost.maintenance.transitPackaging.fields.width', {}, 'Bredde (cm)'), {
      minWidth: 100,
      sortable: true,
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),
    columnFactory.createNumberColumn('height', 'height', t('manager.modules.adminHost.maintenance.transitPackaging.fields.height', {}, 'Høyde (cm)'), {
      minWidth: 100,
      sortable: true,
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),
    columnFactory.createNumberColumn('volume', 'volume', t('manager.modules.adminHost.maintenance.transitPackaging.fields.volume', {}, 'Volum (m³)'), {
      minWidth: 100,
      sortable: true,
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),
    columnFactory.createTextColumn('externalID', 'externalID', t('manager.modules.adminHost.maintenance.transitPackaging.fields.externalID', {}, 'ERP-artikkel (fakturering)'), {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value || '-'
    }),
    columnFactory.createTextColumn('defaultYesNo', 'defaultYesNo', t('manager.modules.adminHost.maintenance.transitPackaging.fields.default', {}, 'Standard'), {
      minWidth: 90,
      sortable: true,
      formatter: (value: any) => value ? '★' : ''
    })
  ], [t]);

  const handleSetDefault = useCallback(async () => {
    if (selectedIds.length !== 1) {
      alert(t('manager.modules.adminHost.maintenance.transitPackaging.selectExactlyOne', {}, 'Velg nøyaktig én lastbærer'));
      return;
    }
    try {
      await transitPackagingService.setDefault(selectedIds[0]);
      setSelectedIds([]);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  }, [selectedIds, loadData, t]);

  const handleDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const confirmed = window.confirm(
      t('manager.modules.adminHost.maintenance.transitPackaging.confirmDelete', { '0': selectedIds.length.toString() }, `Slette ${selectedIds.length} lastbærer(e)? Bærere i bruk på forsendelseslinjer avvises.`)
    );
    if (!confirmed) return;

    const errors: string[] = [];
    for (const id of selectedIds) {
      try {
        await transitPackagingService.delete(id);
      } catch (err: any) {
        errors.push(`#${id}: ${err.message}`);
      }
    }
    if (errors.length > 0) {
      setError(errors.join(' | '));
    }
    setSelectedIds([]);
    await loadData();
  }, [selectedIds, loadData, t]);

  const tableActions: TableAction<TransitPackagingRow>[] = useMemo(() => [
    {
      id: 'create',
      label: t('manager.modules.adminHost.maintenance.transitPackaging.createTitle', {}, 'Ny lastbærer'),
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'set-default',
      label: t('manager.modules.adminHost.maintenance.transitPackaging.setDefault', {}, 'Sett som standard'),
      icon: Star,
      onClick: () => handleSetDefault(),
      disabled: () => selectedIds.length !== 1,
      variant: 'secondary' as const
    },
    {
      id: 'delete',
      label: t('manager.modules.adminHost.maintenance.transitPackaging.delete', { '0': selectedIds.length.toString() }, `Slett (${selectedIds.length})`),
      icon: Trash2,
      onClick: () => handleDelete(),
      disabled: () => selectedIds.length === 0,
      variant: 'danger' as const
    }
  ], [selectedIds.length, handleSetDefault, handleDelete, t]);

  const handleCreate = async () => {
    if (!createForm.descr.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      await transitPackagingService.create({
        transitPackagingDescr: createForm.descr.trim(),
        weight: createForm.weight ? parseFloat(createForm.weight) : 0,
        length: createForm.length ? parseFloat(createForm.length) : null,
        width: createForm.width ? parseFloat(createForm.width) : null,
        height: createForm.height ? parseFloat(createForm.height) : null,
        externalID: createForm.externalID.trim() || null
      });
      setShowCreateModal(false);
      setCreateForm({ descr: '', weight: '', length: '', width: '', height: '', externalID: '' });
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: false,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'transit-packaging-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.transitPackagingID}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Enkel opprett-modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                {t('manager.modules.adminHost.maintenance.transitPackaging.createTitle', {}, 'Ny lastbærer')}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 rounded hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminHost.maintenance.transitPackaging.fields.descr', {}, 'Beskrivelse')} *
                </label>
                <input
                  type="text"
                  value={createForm.descr}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, descr: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.maintenance.transitPackaging.fields.weight', {}, 'Egenvekt (kg)')}
                  </label>
                  <NumericInput
                    mode="decimal"
                    value={createForm.weight}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, weight: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.maintenance.transitPackaging.fields.length', {}, 'Lengde (cm)')}
                  </label>
                  <NumericInput
                    mode="decimal"
                    value={createForm.length}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, length: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.maintenance.transitPackaging.fields.width', {}, 'Bredde (cm)')}
                  </label>
                  <NumericInput
                    mode="decimal"
                    value={createForm.width}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, width: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.maintenance.transitPackaging.fields.height', {}, 'Høyde (cm)')}
                  </label>
                  <NumericInput
                    mode="decimal"
                    value={createForm.height}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, height: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminHost.maintenance.transitPackaging.fields.externalID', {}, 'ERP-artikkel (fakturering)')}
                </label>
                <input
                  type="text"
                  value={createForm.externalID}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, externalID: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('manager.modules.adminHost.maintenance.transitPackaging.externalIDPlaceholder', {}, 'ERP-varenummer for fakturering (valgfritt)')}
                />
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 rounded-b-lg flex justify-end gap-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleCreate}
                disabled={isSaving || !createForm.descr.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving
                  ? t('common.buttons.saving', {}, 'Lagrer...')
                  : t('common.buttons.create', {}, 'Opprett')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
